import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Stack,
} from 'component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useGetAllPlotLabelValues,
  useGetPlotLabelNames,
  usePostPlotLabelName,
  usePostPlotLabelValue,
} from '@/api/rest/resources/organization';
import { useBulkAssignDraftPlots } from '@/api/rest/resources/plot';
import { PlotLabelValue } from '@/api/rest/resources/types/plotGroup';

import { AutocompleteCreateField } from './AutocompleteCreateField';

type BulkEditLabelDialogProps = {
  open: boolean;
  organizationId: string;
  plotIds: string[];
  onClose: () => void;
  onSaved: () => void;
};

export const BulkEditLabelDialog = ({ open, organizationId, plotIds, onClose, onSaved }: BulkEditLabelDialogProps) => {
  const { t } = useTranslation();
  const [labelName, setLabelName] = useState('');
  const [error, setError] = useState<string | undefined>();

  const { data: labelNames } = useGetPlotLabelNames({ queryParams: { organization_id: organizationId } });
  const { data: existingLabelValuesData } = useGetAllPlotLabelValues({
    queryParams: { organization_id: organizationId },
  });
  const existingLabelValues = existingLabelValuesData.results;
  const { mutateAsync: createLabelName, isPending: isCreatingLabelName } = usePostPlotLabelName();
  const { mutateAsync: createLabelValue, isPending: isCreatingLabelValue } = usePostPlotLabelValue();
  const { mutateAsync: bulkAssign, isPending: isAssigning } = useBulkAssignDraftPlots();
  const isPending = isCreatingLabelName || isCreatingLabelValue || isAssigning;

  const handleClose = () => {
    setLabelName('');
    setError(undefined);
    onClose();
  };

  const assignLabel = async (label: PlotLabelValue) => {
    setError(undefined);
    try {
      await bulkAssign({ bodyData: { ids: plotIds, label_ids: [label.id] } });
      setLabelName('');
      onSaved();
    } catch {
      setError(t('shared.uploadPlots.bulkEditLabelDialog.saveFailed', { count: plotIds.length }));
    }
  };

  const handleCreateNew = async () => {
    const name = labelName.trim();
    if (!name) return;
    setError(undefined);
    try {
      const existingLabelName = labelNames.find((n) => n.name.toLowerCase() === name.toLowerCase());
      const labelNameId = existingLabelName
        ? existingLabelName.id
        : (await createLabelName({ bodyData: { name, organization: organizationId } })).id;
      const created = await createLabelValue({
        pathVariables: { plot_label_name_id: labelNameId },
        bodyData: { value: name },
      });
      await assignLabel(created);
    } catch {
      setError(t('shared.uploadPlots.bulkEditLabelDialog.saveFailed', { count: plotIds.length }));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) handleClose();
      }}
    >
      <DialogContent>
        <DialogHeader className='typography-h2 w-full text-success md:typography-h3'>
          <DialogTitle>{t('shared.uploadPlots.bulkEditLabelDialog.title')}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Stack spacing={2}>
            <p className='typography-body2 text-text-secondary'>
              {t('shared.uploadPlots.bulkEditLabelDialog.subtitle', { count: plotIds.length })}
            </p>
            <label className='typography-overline text-text-secondary'>
              {t('shared.uploadPlots.bulkEditLabelDialog.fieldLabel')}
            </label>

            <AutocompleteCreateField
              items={existingLabelValues}
              getItem={(item) => ({ key: item.id, label: item.value, meta: item.label_name })}
              searchValue={labelName}
              onSearchValueChange={setLabelName}
              onSelectItem={assignLabel}
              onCreateNew={handleCreateNew}
              disabled={isPending}
              placeholder={t('shared.uploadPlots.bulkEditLabelDialog.placeholder')}
              noResultsLabel={t('shared.uploadPlots.bulkEditLabelDialog.noResults')}
              createOptionLabel={(name) => t('shared.uploadPlots.createOption', { name })}
              inputClassName='w-full rounded-lg border border-neutral-black-23 px-3 py-2 text-sm outline-none focus:border-primary-100'
            />

            {error && <span className='typography-caption text-error'>{error}</span>}
          </Stack>
        </DialogBody>
        <DialogFooter className='pt-4 flex-row justify-end'>
          <Button variant='outline' onClick={handleClose}>
            {t('global.ui.buttons.cancel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
